import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState, MouseEvent } from "react";
import { Menu, MenuItem } from "@mui/material";
import clsx from "clsx";
import { DefaultCopyOption } from "dora-contracts";
import "./DuplicateCargoDropdown.scss";

const DuplicateCargoDropdown = (props: {
  disabled: boolean;
  duplicateInProgress: boolean;
  // Which of the four actions is the dropdown's default (main-button) action.
  defaultOption: DefaultCopyOption;
  // Whether the "copy to drivers' routes" option is available (feature-gated).
  showCopyToDriverRoutes: boolean;
  // Offer only the plain copy (e.g. the original has been deleted).
  onlyCopyOrder?: boolean;
  onCopyOrder: () => void;
  onCopyWithInvoiceLines: () => void;
  onCopyWithInvoiceLinesResetQty: () => void;
  onCopyToDriverRoutes: () => void;
}) => {
  const { t } = useTranslation("shipmentModal");
  const {
    disabled,
    duplicateInProgress,
    defaultOption,
    showCopyToDriverRoutes,
    onlyCopyOrder = false,
    onCopyOrder,
    onCopyWithInvoiceLines,
    onCopyWithInvoiceLinesResetQty,
    onCopyToDriverRoutes,
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [componentLevelDisabled, setComponentLevelDisabled] = useState(false);
  const open = Boolean(anchorEl);

  const isDisabled = disabled || duplicateInProgress || componentLevelDisabled;

  const handleClick = (event: MouseEvent<any>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // to avoid multiple clicking that triggers before the disabled prop is pushed down from the master component
  const temporarySetComponentLevelDisabled = () => {
    setComponentLevelDisabled(true);
    setTimeout(() => setComponentLevelDisabled(false), 500);
  };

  const options: {
    key: DefaultCopyOption;
    label: string;
    onClick: () => void;
  }[] = [
    { key: "COPY_ORDER", label: t("duplicate"), onClick: onCopyOrder },
    {
      key: "COPY_WITH_INVOICE_LINES",
      label: t("duplicateWithInvoiceLines"),
      onClick: onCopyWithInvoiceLines,
    },
    {
      key: "COPY_WITH_INVOICE_LINES_RESET_QTY",
      label: t("duplicateWithInvoiceLinesQuantityOne"),
      onClick: onCopyWithInvoiceLinesResetQty,
    },
    ...(showCopyToDriverRoutes
      ? [
          {
            key: "COPY_TO_DRIVER_ROUTES" as const,
            label: t("copyToDriverRoutes.button"),
            onClick: onCopyToDriverRoutes,
          },
        ]
      : []),
  ];
  const allOptions = onlyCopyOrder
    ? options.filter((o) => o.key === "COPY_ORDER")
    : options;

  // Falls back to the first option when the configured default is unavailable
  // (e.g. default is "copy to drivers' routes" but the feature is off).
  const mainOption =
    allOptions.find((o) => o.key === defaultOption) ?? allOptions[0];
  const menuOptions = allOptions.filter((o) => o.key !== mainOption.key);

  const runOption = (onClick: () => void) => {
    temporarySetComponentLevelDisabled();
    setAnchorEl(null);
    if (isDisabled) {
      return;
    }
    onClick();
  };

  const classes = clsx("duplicate-cargo-dropdown", {
    disabled: isDisabled,
  });

  return (
    <div>
      <div className={classes} aria-disabled={disabled}>
        <div
          className="duplicate-main-button dora-button--primary"
          role="button"
          onClick={() => runOption(mainOption.onClick)}
        >
          {duplicateInProgress ? t("duplicateProgress") : mainOption.label}
        </div>
        <div
          className="duplicate-expand dora-button--primary"
          role="button"
          onClick={handleClick}
        >
          <ExpandMoreIcon fontSize={"small"} />
        </div>
      </div>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuOptions.map((option, index) => (
          <MenuItem
            key={option.key}
            onClick={() => runOption(option.onClick)}
            dense
            autoFocus={index === 0}
            sx={{
              my: -0.5,
              py: 1,
              "&:focus": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default DuplicateCargoDropdown;
